import { useState, useEffect } from "react";
import { supabase, type ContactMessage } from "@/lib/supabase";

export default function MessagesManager() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState<ContactMessage | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    setIsLoading(true);
    const { data } = await supabase.from("contact_messages").select("*").order("created_at", { ascending: false });
    setMessages(data || []);
    setIsLoading(false);
  };

  const filtered = messages.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.subject.toLowerCase().includes(search.toLowerCase()) ||
      m.message.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this message?")) return;
    const { error } = await supabase.from("contact_messages").delete().eq("id", id);
    if (!error) await fetchMessages();
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-10 bg-gray-100 rounded w-full max-w-md" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <h1 className="font-heading font-bold text-2xl text-primary-900 mb-6">Contact Messages</h1>

      <div className="mb-4">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input type="text" placeholder="Search messages..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Subject</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((msg) => (
                <tr key={msg.id} className="hover:bg-gray-50/50">
                  <td className="px-4 md:px-6 py-3">
                    <div>
                      <p className="font-medium text-sm text-gray-800">{msg.name}</p>
                      <p className="text-xs text-gray-400">{msg.email}</p>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-3 text-sm text-gray-700 line-clamp-1">{msg.subject}</td>
                  <td className="px-4 md:px-6 py-3 text-sm text-gray-500 whitespace-nowrap">{new Date(msg.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                  <td className="px-4 md:px-6 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setViewing(msg)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary-600 transition-colors" aria-label="View"><i className="ri-eye-line text-sm" /></button>
                      <button onClick={() => handleDelete(msg.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors" aria-label="Delete"><i className="ri-delete-bin-line text-sm" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No messages found.</div>}
      </div>

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-heading font-bold text-lg text-primary-900">Message Details</h2>
              <button onClick={() => setViewing(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"><i className="ri-close-line" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-gray-500 font-medium uppercase">From</label><p className="text-sm text-gray-800 mt-0.5">{viewing.name}</p></div>
                <div><label className="text-xs text-gray-500 font-medium uppercase">Email</label><p className="text-sm text-gray-800 mt-0.5">{viewing.email}</p></div>
              </div>
              <div><label className="text-xs text-gray-500 font-medium uppercase">Subject</label><p className="text-sm text-gray-800 mt-0.5 font-medium">{viewing.subject}</p></div>
              <div><label className="text-xs text-gray-500 font-medium uppercase">Message</label><p className="text-sm text-gray-700 mt-1 leading-relaxed bg-gray-50 rounded-lg p-3">{viewing.message}</p></div>
              <div><label className="text-xs text-gray-500 font-medium uppercase">Received</label><p className="text-sm text-gray-500 mt-0.5">{new Date(viewing.created_at).toLocaleString("en-US")}</p></div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button onClick={() => setViewing(null)} className="px-5 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}